import React from "react"
import styled from "styled-components"
import Link from "next/link"
import { useTranslations } from "next-intl"

import DesktopContainer from "~/components/DesktopContainer"
import Spacer from "~/components/Spacer"
import FooterTableLinks from "./FooterTableLinks"
import OutlinkImages from "./OutlinkImages"
import {
  SUPPORT_PHONE_NUMBER,
  SUPPORT_PHONE_NUMBER_WL,
} from "~/components/altHeaders/phoneNumbers"
import useInject from "~/hooks/useInject"

const FooterContainer = styled.div`
  font-family: ${(props) => props.theme.fonts.avenirNextRegular};
  color: ${(props) => props.theme.colors.text.mainFooter};
  background: ${(props) => props.theme.colors.background.footer};
  padding: 41px 0 51px 0;
`

const BottomFooterContainer = styled.div`
  font-family: ${(props) => props.theme.fonts.avenirNextRegular};
  color: ${(props) => props.theme.colors.text.subFooter};
  background: ${(props) => props.theme.colors.background.subFooter};
  padding: 22px 0;
`

const CopyrightContainer = styled.p`
  font-size: 0.9em;
  line-height: 1.1875em;
  color: ${(props) => props.theme.colors.text.mainFooter};
  margin: 0;
  padding: 0;
`

const LinksContainer = styled.div`
  margin-bottom: 10px;

  a {
    margin-right: 15px;
    font-size: 0.9em;
  }
`

const Separator = styled.div`
  min-height: 2px;
  background: #7e7e7e;
  width: 38%;
  margin-top: 5px;
  /* Example breakpoint for small desktops */
  @media (max-width: 1024px) {
    width: 50%;
  }
  /* Mobile */
  @media (max-width: 600px) {
    width: 70%;
  }
`

const SupportContainer = styled(CopyrightContainer)``

const DisclaimerContainer = styled.p`
  margin: 0;
  padding: 0;
  line-height: 1.125em;
  text-align: justify;
`

const FooterLink = styled.a`
  display: inline-block;
  color: ${(props) => props.theme.colors.old.footerText};
  text-decoration: none;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`

const mapStore = (store) => ({
  isStateAffiliatedWithWinitLawLookup:
    store.trafficLookup.isStateAffiliatedWithWinitLaw,
  isStateAffiliatedWithWinitLawTrafficSubmission:
    store.trafficSubmission.isStateAffiliatedWithWinitLaw,
})

const Footer = ({ children }) => {
  const {
    isStateAffiliatedWithWinitLawLookup,
    isStateAffiliatedWithWinitLawTrafficSubmission,
  } = useInject(mapStore)
  const currentYear = new Date().getFullYear()
  const translate = useTranslations()

  return (
    <>
      {children}
      {!isStateAffiliatedWithWinitLawLookup &&
        !isStateAffiliatedWithWinitLawTrafficSubmission && (
          <FooterContainer>
            <DesktopContainer>
              <FooterTableLinks />
              <Spacer desktop={30} mobile={20} />
              <OutlinkImages />
            </DesktopContainer>
          </FooterContainer>
        )}
      <BottomFooterContainer>
        <DesktopContainer>
          {isStateAffiliatedWithWinitLawLookup ||
            (isStateAffiliatedWithWinitLawTrafficSubmission && (
              <LinksContainer>
                <Link
                  href={
                    process.env.WINIT_ENV === "development" ||
                    process.env.WINIT_ENV === "staging"
                      ? "https://staging.newdesign.winitlaw.com/terms-of-use"
                      : "https://winitlaw.com/terms-of-use"
                  }
                  passHref
                >
                  <FooterLink target="_blank" rel="noreferrer">
                    Terms of Use
                  </FooterLink>
                </Link>
                <Link
                  href={
                    process.env.WINIT_ENV === "development" ||
                    process.env.WINIT_ENV === "staging"
                      ? "https://staging.newdesign.winitlaw.com/privacy-policy"
                      : "https://winitlaw.com/privacy-policy"
                  }
                  target="_blank"
                  rel="noreferrer"
                  passHref
                >
                  <FooterLink target="_blank" rel="noreferrer">
                    Privacy Policy
                  </FooterLink>
                </Link>
                <Separator />
              </LinksContainer>
            ))}
          <CopyrightContainer>
            ©&nbsp;
            {currentYear}
            &nbsp;
            {isStateAffiliatedWithWinitLawLookup ||
            isStateAffiliatedWithWinitLawTrafficSubmission
              ? "Winit Law, 225 Broadway, New York NY 10007"
              : "Winit LLC, 330 7th Avenue. New York, NY 10001"}
            . | {translate("footer.copyright")}.{" "}
          </CopyrightContainer>
          <Spacer mobile={20} desktop={10} />
          <SupportContainer>
            {translate("footer.contact")}:&nbsp;
            {isStateAffiliatedWithWinitLawLookup ||
            isStateAffiliatedWithWinitLawTrafficSubmission ? (
              <>
                <FooterLink href="mailto:support@winitlaw.com">
                  support@winitlaw.com
                </FooterLink>
              </>
            ) : (
              <>
                <FooterLink href="mailto:support@appwinit.com">
                  support@appwinit.com
                </FooterLink>
              </>
            )}
            &nbsp;|&nbsp;
            {isStateAffiliatedWithWinitLawLookup ||
            isStateAffiliatedWithWinitLawTrafficSubmission ? (
              <>
                <FooterLink href={`tel:${SUPPORT_PHONE_NUMBER_WL.number}`}>
                  {SUPPORT_PHONE_NUMBER_WL.displayNumber}
                </FooterLink>
                &nbsp; ({SUPPORT_PHONE_NUMBER_WL.rawDisplayNumber})
              </>
            ) : (
              <>
                <FooterLink href={`tel:${SUPPORT_PHONE_NUMBER.number}`}>
                  {SUPPORT_PHONE_NUMBER.displayNumber}
                </FooterLink>
                &nbsp; ({SUPPORT_PHONE_NUMBER.rawDisplayNumber})
              </>
            )}
          </SupportContainer>
          <Spacer mobile={20} desktop={10} />
          <DisclaimerContainer>
            {isStateAffiliatedWithWinitLawLookup ||
            isStateAffiliatedWithWinitLawTrafficSubmission ? (
              <>
                {translate("footer.winItLawDisclaimer")}
                &nbsp;
                <Link
                  href={
                    process.env.WINIT_ENV === "development" ||
                    process.env.WINIT_ENV === "staging"
                      ? "https://staging.newdesign.winitlaw.com/terms-of-use"
                      : "https://winitlaw.com/terms-of-use"
                  }
                  target="_blank"
                  rel="noreferrer"
                  passHref
                >
                  <FooterLink target="_blank" rel="noreferrer">
                    {translate("general.termsOfService")}
                  </FooterLink>
                </Link>
                {` ${translate("general.and")} `}
                <Link
                  href={
                    process.env.WINIT_ENV === "development" ||
                    process.env.WINIT_ENV === "staging"
                      ? "https://staging.newdesign.winitlaw.com/privacy-policy"
                      : "https://winitlaw.com/privacy-policy"
                  }
                  target="_blank"
                  rel="noreferrer"
                  passHref
                >
                  <FooterLink target="_blank" rel="noreferrer">
                    {translate("general.privacyPolicy")}.
                  </FooterLink>
                </Link>
                <br />
                <br />
                {translate("footer.bwLawReviewsDisclaimer")}
              </>
            ) : (
              <>
                {translate("footer.disclaimer")}
                <Link href="/terms-of-use" target="_blank" passHref>
                  <FooterLink target="_blank" rel="noreferrer">
                    {translate("general.termsOfService")}
                  </FooterLink>
                </Link>
                {` ${translate("general.and")} `}
                <Link href="/privacy-policy" target="_blank" passHref>
                  <FooterLink target="_blank" rel="noreferrer">
                    {translate("general.privacyPolicy")}.
                  </FooterLink>
                </Link>
              </>
            )}
          </DisclaimerContainer>
        </DesktopContainer>
      </BottomFooterContainer>
    </>
  )
}

export default Footer
